import { useState } from 'react'
import { BlockEditor, type BlockEditorProps } from '@w/content-blocks-editor'
import { pageDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import type { Block, Blocks } from '@w/content-blocks'
import { BLOCK_LEVEL } from '@w/content-blocks/src/block-schema'
import { Doc } from '@w/crdt'

/** Project retained answers as visible content; read mode itself hides question answers. */
export function DeletedReviewEditor(props: Pick<BlockEditorProps, 'doc' | 'spaceId' | 'fileHandler' | 'className'>) {
  const [view, setView] = useState(() => retainedView(props.doc))
  if (view.source !== props.doc) setView(retainedView(props.doc))
  return <BlockEditor
    {...props}
    doc={view.doc}
    mode="read"
    onChange={noop}
  />
}

function retainedView(source: Doc) {
  return { source, doc: new Doc({ content: { children: structuredClone(
    rootBlocks(deletedReviewBlocks(pageDocument(source).content.children as unknown as Blocks)),
  ) as never } }) }
}

const blockLevel: ReadonlySet<string> = new Set(BLOCK_LEVEL)
/** The page root admits only block-level blocks (block-schema CONTAINMENT, enforced on write by crdt_rs rules); an
 * inline answer copy (an `sa` answer block, a cloze) is shown in a paragraph, its inline host. */
function rootBlocks(blocks: Blocks): Blocks {
  return blocks.map((block): Block => blockLevel.has(block.type) ? block : { id: `${block.id}:host`, type: 'p', children: [block] })
}

export function deletedReviewBlocks(blocks: Blocks): Blocks {
  return blocks.flatMap(block => {
    let shown = 'children' in block && Array.isArray(block.children)
      ? { ...block, children: deletedReviewBlocks(block.children as Blocks) } as Block : block
    if (block.type === 'cloze' && block.answerAudio?.src) {
      return [shown, { id: `${block.id}:reference`, type: 'audio', src: block.answerAudio.src }]
    }
    if (block.type !== 'question') return [shown]
    shown = { ...shown,
      ...(block.question && { question: deletedReviewBlocks(block.question) }),
      explanation: [],
      ...((block.subtype === 'sc' || block.subtype === 'mc') && { opts: block.opts.map(option => deletedReviewBlocks(option)) }),
    } as Block
    const explanation = deletedReviewBlocks(block.explanation ?? [])
    switch (block.subtype) {
      case 'sa':
        return [shown, ...block.answer.flatMap((answer: unknown, index) => {
          if (typeof answer === 'string') return [textAnswer(answer, `${block.id}:answer:${index}`)]
          if (typeof answer === 'object' && answer !== null && 'type' in answer) return deletedReviewBlocks([answer as Block])
          return []
        }), ...(block.answerRegexp ? [textAnswer(block.answerRegexp, `${block.id}:regexp`)] : []), ...explanation]
      case 'sc':
        return [shown, ...answerCopy(deletedReviewBlocks(block.opts[block.answer] ?? []), block.id), ...explanation]
      case 'mc':
        return [shown, ...block.answer.flatMap(index => answerCopy(deletedReviewBlocks(block.opts[index] ?? []), block.id)), ...explanation]
      case 'la':
        return [shown, ...block.criteria.map((criterion, index) => textAnswer(criterion, `${block.id}:criterion:${index}`)), ...explanation]
      case 'sr':
        return [shown, textAnswer(block.answer.transcript, `${block.id}:transcript`), ...(block.answer.src
          ? [{ id: `${block.id}:reference`, type: 'audio' as const, src: block.answer.src }] : []), ...explanation]
    }
    return [shown]
  })
}

/** Correct options also appear in the prompt; their answer copy needs its own document identity. */
function answerCopy(blocks: Blocks, owner: string): Blocks {
  function copy(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(item => copy(item))
    if (value === null || typeof value !== 'object') return value
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [
      key, key === 'id' && typeof item === 'string' ? `${owner}:answer:${item}` : copy(item),
    ]))
  }
  return copy(blocks) as Blocks
}

function textAnswer(text: string, id: string): Block {
  return { id, type: 'p', children: [{ id: `${id}:text`, type: 'text', text }] }
}

function noop() {}
